import type { TeamDDLOption } from '@/apis/lookup';
import type { TeamWithUsers } from '@/apis/types';
import type { ListItem } from '../../ControlTower/types';

export interface AlertAssignmentOption extends ListItem {
  kind?: 'team' | 'user' | 'role' | 'unassigned';
  parentId?: string;
  color?: string;
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

interface BuildParams {
  /** The teams the user can see for the Alerts feature. */
  teams: TeamDDLOption[];
  /** The same teams with their members. */
  teamsUsers: TeamWithUsers[];
}

/**
 * Unassigned, then each team followed by its members. Entries that are not
 * real people are left out: the notification API adds a team's general support
 * email as a member, with an id that is not a user id, and the list request
 * fails if one of those is ever sent as an owner.
 */
export function buildAlertAssignmentOptions({
  teams,
  teamsUsers,
}: BuildParams): AlertAssignmentOption[] {
  const usersByTeam = new Map(
    teamsUsers.map((team) => [
      team.id,
      team.users.filter((user) => UUID_PATTERN.test(String(user.id))),
    ])
  );

  return [
    { id: 'unassigned', name: 'Unassigned', kind: 'unassigned' },
    ...teams.flatMap((team): AlertAssignmentOption[] => {
      const users = usersByTeam.get(team.id) ?? [];
      return [
        {
          id: `team:${team.id}`,
          name: team.name,
          kind: 'team',
          color: team.color,
          enabled: users.length > 0,
        },
        ...users.map((user): AlertAssignmentOption => ({
          id: `user:${user.id}`,
          name: user.name || user.email,
          kind: 'user',
          parentId: `team:${team.id}`,
        })),
      ];
    }),
  ];
}
